/**
 * Stamps the signed-in user's opaque id onto every span, so it does not have
 * to be threaded through every call site that starts one.
 *
 * `setTelemetryUserId` takes whatever the caller supplies; it is the caller's
 * job to pass an opaque id (MSAL's `homeAccountId`/`localAccountId`, this
 * app's own `userId`) and never an email or username — see
 * src/components/shell/EntraShell.tsx, where `enduser.id` is set from
 * `auth.userId`, the same opaque value already used for the platform
 * session key.
 *
 * @module telemetry/user-context-span-processor
 */
import type { Span, SpanProcessor } from "@opentelemetry/sdk-trace-web";

let currentUserId: string | undefined;

/** Set the id stamped onto every span started from now on. `undefined` clears it (e.g. on sign-out). */
export function setTelemetryUserId(id: string | undefined): void {
  currentUserId = id || undefined;
}

/** For tests: read back the currently configured id. */
export function getTelemetryUserId(): string | undefined {
  return currentUserId;
}

/** Stamps `enduser.id` on every span at start, from whatever `setTelemetryUserId` last set. */
export class UserContextSpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    if (currentUserId) {
      span.setAttribute("enduser.id", currentUserId);
    }
  }

  onEnd(): void {}

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
}
